import http from '../../../api/axios';
import { apiUrl } from '../../../api/apiUrl';
import { Values } from '../../../forms/UserTask/UserTask.types';
import { handleApiErrors } from '../../../api/apiErrorHandler';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { UserTaskForm } from '../../../forms/UserTask/UserTaskForm';
import { Loader } from '../../../components/loader/Loader';
import { useTranslation } from '../../../context/translationContext';
import { UserTask, UserTaskState, UserTaskStateTranslated } from '../../../types/UserTask';
import { TabsComponent } from '../../../components/tabs/Tabs';
import { ObjectFromEnum, convertEnumToArray } from '../../../utils/functions/data.functions';
import { UserTaskFiles } from './UserTaskFiles';
import { UserTaskHistoryPage } from './UserTaskHistoryPage';
import Select from '../../../components/select/Select';
import Button from '../../../components/button/Button';
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs';
import { paths } from '../../RouterContainer';
import { useAuth } from '../../../context/AuthContext';
import { useLoading } from '../../../context/loadingContext';
import { useCurrentCompany } from '../../../hooks/useCompany';

export enum TASK_TABS {
  FILES = 'tab-files',
  HISTORY = 'tab-history',
}

/**
 * Page component for editing existing user task of company
 */
export const UserTaskUpdatePage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { company } = useCurrentCompany();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState<UserTask | null>(null);
  const [activeTab, setActiveTab] = useState<string>(TASK_TABS.FILES);
  const { setBreadcrumbs } = useBreadcrumbs();
  const { startLoading, stopLoading } = useLoading();
  useEffect(() => {
    fetchData();
    setBreadcrumbs([
      { path: paths.USER_TASKS, name: 'navigation.user-tasks' },
      {
        path: `${paths.USER_TASKS}/edit/${id}`,
        name: 'navigation.user-tasks-edit',
      },
    ]);
  }, []);

  /**
   * Loads company user tasks
   */
  const fetchData = async () => {
    const response = await http.apiGet({
      url: `${apiUrl.USER_TASK_ITEM}?id=${id}`,
    });
    setData(response?.data);
  };

  /**
   * Handles edit action for UserTask form submit.
   * @param values form data
   */
  const handleSubmit = async (values: Values) => {
    try {
      startLoading();
      const response = await http.apiPut({ url: apiUrl.USER_TASK_UPDATE, data: values });
      if (response?.status === 200) {
        stopLoading();
        toast.success('Success!');
      }
    } catch (err) {
      stopLoading();
      handleApiErrors(err);
    }
  };

  /**
   * Change status of existing task
   * @param e
   */
  const handleTaskChangeStatus = async (state: ObjectFromEnum) => {
    if (!data || !state) return;
    try {
      startLoading();
      const response = await http.apiPut({
        url: `${apiUrl.USER_TASK_UPDATE_STATE}?userTaskId=${data.id}&userTaskState=${state.value}`,
      });
      if (response?.status === 204) {
        stopLoading();
        toast.success('Success!');
      }
    } catch (err) {
      stopLoading();
      handleApiErrors(err);
    }
  };

  /**
   * If company changes while on task detail redirect to tasks overview
   */
  useEffect(() => {
    if (!data || !company || company?.id === data?.companyId) return;
    navigate(`/${paths.USER_TASKS}`);
  }, [company, data]);

  /**
   * Assigns task to current user
   * TODO - assign task to any user
   */
  const handleAssignToCurrentUser = async () => {
    if (!data || !user) return;
    try {
      startLoading();
      const response = await http.apiPut({
        url: `${apiUrl.USER_TASK_ASSIGN}?userTaskId=${data.id}&userId=${user.id}`,
      });
      if (response?.status === 204) {
        stopLoading();
        fetchData();
        toast.success('Success!');
      }
    } catch (err) {
      stopLoading();
      handleApiErrors(err);
    }
  };

  if (data === null) return <Loader />;
  return (
    <>
      <h1 className="text-3xl font-bold">{t('page.user-task.headline-edit')}</h1>
      {data.responsibleUser && (
        <p className="absolute">
          {t(`page.user-task.col-responsible-user`)}:{' '}
          <span className="font-bold">{data.responsibleUser?.fullName}</span>
        </p>
      )}
      <div className="flex flex-row-reverse	items-center">
        <div className="absolute flex items-center mr-10">
          <div className="absolute right-0 min-w-max text-left">
            <Select
              title={t('page.user-task.change-status')}
              data={convertEnumToArray(UserTaskState, UserTaskStateTranslated, t)}
              labelKey="name"
              valueKey="value"
              value={{
                name: t(UserTaskStateTranslated[data.state]),
                value: data.state,
              }}
              onChange={(e) => handleTaskChangeStatus(e)}
            />
          </div>
          <div className="absolute right-52 min-w-max">
            <br />
            <Button
              title={t('page.user-task.take-over-task')}
              onClick={handleAssignToCurrentUser}
              showLoading
            />
          </div>
        </div>
      </div>
      <div className="flex !mb-0">
        <div className="w-1/3 mr-5">
          <UserTaskForm onSubmit={handleSubmit} initialValues={data as Values} />
        </div>
        <div className="w-2/3">
          <TabsComponent
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            tabs={convertEnumToArray(TASK_TABS)}
          >
            <div key={TASK_TABS.FILES}>
              <UserTaskFiles />
            </div>
            <div key={TASK_TABS.HISTORY}>
              <UserTaskHistoryPage />
            </div>
          </TabsComponent>
        </div>
      </div>
    </>
  );
};
